import { ComponentProps, ComponentType, lazy, ReactElement, Suspense } from "react";
import { DelayedLoader } from "../app/components/DelayedLoader/index.js";
import { FlexLoading } from "../customHooks/useLoadingPage/index.js";
import { Unpromisify } from "./types/Unpromisify.js";

export const lazyLoad = <T extends Promise<any>, U extends ComponentType<any>>(
    importFunc: () => T,
    selectorFunc?: (s: Unpromisify<T>) => U,
    opts: {
        fallback: ReactElement | null;
        fallbackDelay?: number;
    } = { fallback: <FlexLoading extra={{ from: "lazyLoad" }} /> },
): ((props: ComponentProps<U>) => JSX.Element) => {
    let lazyFactory: () => Promise<{
        default: U;
    }> = importFunc;

    if (selectorFunc) {
        lazyFactory = () => importFunc().then((module) => ({ default: selectorFunc(module) }));
    }

    const LazyComponent = lazy(lazyFactory);

    return (props: ComponentProps<U>): JSX.Element => (
        <Suspense fallback={<DelayedLoader loader={opts.fallback} delay={opts.fallbackDelay ?? 400} />}>
            <LazyComponent {...props} />
        </Suspense>
    );
};
